/**
 * krida-tutorial-engine.js - Scripted Tutorial Scenario Engine
 *
 * Renders scripted board states, walks players through guided actions,
 * validates interactions against expected outcomes, and handles
 * opponent auto-play for multi-step scenarios.
 *
 * Operates independently of the live game loop — when a tutorial scenario
 * is active, the game page is in "tutorial mode".
 */

import * as KridaUIBoard from './krida-ui-board.js';
import * as KridaUIHand from './krida-ui-hand.js';
import * as KridaUIHarvest from './krida-ui-harvest.js';
import * as TutorialOverlay from './krida-ui-tutorial-overlay.js';
import { getCardNumericValue, getCardPointValue, computePileValue, findValidStockpileOptions } from './krida-patra-helpers.js';

// --- Card string parsing ---

const SUIT_MAP = { '\u2660': 'spades', '\u2665': 'hearts', '\u2666': 'diamonds', '\u2663': 'clubs' };
const SUIT_SYMBOLS = { spades: '\u2660', hearts: '\u2665', diamonds: '\u2666', clubs: '\u2663' };

function parseCardString(str) {
    const suitChar = str.slice(-1);
    const valueStr = str.slice(0, -1);
    const card = { suit: SUIT_MAP[suitChar], value: valueStr };
    card.numericValue = getCardNumericValue(card);
    return card;
}

// --- State ---

let active = false;
let scenarioData = null;       // Full interactive scenario definition
let currentStepIndex = 0;      // Index within scenario's steps array
let currentGuideIndex = 0;     // Index within current step's guideSteps
let selectedCardIndex = -1;
let selectedPileIndices = [];
let currentHand = [];          // Parsed card objects for current step
let currentBoard = [];         // Array of pile objects { visible: [], hidden: [] }
let tutorialHarvests = [];     // Accumulated harvests for display
let handContentEl = null;
let boardContentEl = null;
let actionControlsEl = null;
let usingTempGameArea = false; // True when we created a temporary game area
let tempGameArea = null;       // Reference to temp container for cleanup
let loadedStylesheets = [];    // Dynamically loaded CSS for cleanup
let navCallbacks = null;       // { onPrev, onClose, canPrev } from wizard

// Event listeners we attach (for cleanup)
let handListener = null;
let boardListener = null;
let actionListener = null;

// --- Public API ---

/**
 * Activate the scenario engine with a scenario definition.
 * Creates a temporary game area if game DOM isn't present (e.g., on dashboard).
 * @param {Object} scenario - Parsed interactive scenario JSON
 */
export async function activate(scenario, callbacks) {
    scenarioData = scenario;
    currentStepIndex = 0;
    active = true;
    navCallbacks = callbacks || null;

    // Check if we're on the game page (has game DOM)
    const existingBoard = document.getElementById('board-content');
    if (!existingBoard) {
        await _createTempGameArea();
    }

    // Cache DOM elements (now guaranteed to exist)
    handContentEl = document.getElementById('hand-content');
    boardContentEl = document.getElementById('board-content');
    actionControlsEl = document.getElementById('action-controls');

    // Initialize UI modules if they haven't been initialized
    // (they won't be if we're not on the game page)
    if (usingTempGameArea) {
        KridaUIBoard.init({ elements: { boardContent: boardContentEl } });
        KridaUIHand.initHand(handContentEl);
        const harvestContentEl = document.getElementById('harvest-content');
        if (harvestContentEl) {
            KridaUIHarvest.init({ elements: { harvestContent: harvestContentEl } });
        }
    }

    // Reset harvests
    tutorialHarvests = [];
    KridaUIHarvest.updateHarvest([]);

    // Hide non-tutorial UI elements
    _setTutorialMode(true);

    // Attach interaction listeners
    _attachListeners();

    // Start first step
    _runStep();
}

/**
 * Deactivate the scenario engine, restore normal game UI.
 */
export function deactivate() {
    active = false;
    scenarioData = null;
    TutorialOverlay.dismiss();
    _detachListeners();
    _setTutorialMode(false);
    _clearSelections();
    _removeTempGameArea();
}

/**
 * Check if the engine is currently running a scenario.
 * @returns {boolean}
 */
export function isActive() {
    return active;
}

// Build the nav object passed to the overlay. `onNextAdvance` is the
// scenario-internal advancement (called when the user clicks "Next").
function _navForOverlay(onNextAdvance) {
    if (!navCallbacks) return null;
    return {
        canPrev: navCallbacks.canPrev !== false,
        onPrev: () => {
            // Tear down scenario state, then let wizard go back
            const cb = navCallbacks?.onPrev;
            if (cb) cb();
        },
        onClose: () => {
            const cb = navCallbacks?.onClose;
            if (cb) cb();
        },
        onNext: onNextAdvance
    };
}

// --- Step execution ---

function _runStep() {
    if (!scenarioData || !scenarioData.steps || currentStepIndex >= scenarioData.steps.length) {
        // Scenario complete
        _onScenarioComplete();
        return;
    }

    const step = scenarioData.steps[currentStepIndex];

    if (step.type === 'player') {
        _runPlayerStep(step);
    } else if (step.type === 'opponent') {
        _runOpponentStep(step);
    } else if (step.type === 'display') {
        _runDisplayStep(step);
    }
}

function _runDisplayStep(step) {
    // Parse hand and board from step data (no interaction expected).
    // Each board pile can be either an array of card strings (all visible)
    // or an object { visible: [...], hidden: [...] } for stockpiled piles.
    currentHand = (step.hand || []).map(parseCardString);
    currentBoard = (step.board || []).map(pile => {
        if (Array.isArray(pile)) {
            return { visible: pile.map(parseCardString), hidden: [] };
        }
        return {
            visible: (pile.visible || []).map(parseCardString),
            hidden: (pile.hidden || []).map(parseCardString)
        };
    });

    _clearSelections();

    // Render board and hand
    KridaUIBoard.updateBoard(currentBoard, [], true);
    KridaUIHand.updateHand(currentHand, [], true);

    // Disable action buttons (display-only)
    _setActionButtons();
    _updateActionButtons();

    // Position the narration based on step config (default: above the hand area)
    const advance = () => { currentStepIndex++; _runStep(); };
    const nav = _navForOverlay(advance);
    const position = step.textPosition || 'above-hand';
    const title = step.title || scenarioData.title || '';
    if (position === 'below-board') {
        const boardEl = document.getElementById('board');
        TutorialOverlay.showNarrationBelow(step.text || '', boardEl, advance, nav, title);
    } else {
        const handArea = document.getElementById('hand-area');
        TutorialOverlay.showNarrationAbove(step.text || '', handArea, advance, nav, title);
    }
}

function _runPlayerStep(step) {
    // Parse hand and board from step data
    currentHand = step.hand.map(parseCardString);
    currentBoard = step.board.map(pile => ({
        visible: pile.map(parseCardString),
        hidden: []
    }));

    _clearSelections();

    // Render board and hand
    KridaUIBoard.updateBoard(currentBoard, [], true);
    KridaUIHand.updateHand(currentHand, [], true);

    // Set action buttons to tutorial state
    _setActionButtons();
    _updateActionButtons();

    // Start guide steps
    currentGuideIndex = 0;
    _showGuideStep();
}

function _runOpponentStep(step) {
    // Show "opponent thinking" indicator
    const thinkingDelay = step.thinkingDelay || 1500;

    // Display a narration overlay while opponent "thinks"
    TutorialOverlay.showGuideOverlay('Opponent is thinking...', null);

    setTimeout(() => {
        TutorialOverlay.dismiss();

        // Apply the opponent's action to the board
        if (step.action === 'harvest' && step.piles && step.piles.length > 0) {
            // Remove harvested piles from the board (process in reverse to preserve indices)
            const sortedPiles = [...step.piles].sort((a, b) => b - a);
            sortedPiles.forEach(pileIdx => {
                if (pileIdx < currentBoard.length) {
                    currentBoard.splice(pileIdx, 1);
                }
            });
        } else if (step.action === 'sow' && step.card) {
            // Add a sown card to the board
            const card = parseCardString(step.card);
            currentBoard.push({ visible: [card], hidden: [] });
        }

        // Re-render board
        KridaUIBoard.updateBoard(currentBoard, [], false);

        // Show narration
        if (step.narration) {
            const advance = () => { currentStepIndex++; _runStep(); };
            TutorialOverlay.showGuideOverlay(step.narration, null, advance, _navForOverlay(advance));
        } else {
            currentStepIndex++;
            _runStep();
        }
    }, thinkingDelay);
}

// --- Guide step display ---

function _showGuideStep() {
    const step = scenarioData.steps[currentStepIndex];
    if (!step.guideSteps || currentGuideIndex >= step.guideSteps.length) {
        return;
    }

    const guide = step.guideSteps[currentGuideIndex];
    const targetEl = _resolveTarget(guide.target);
    const [type, param] = guide.target.split(':');

    if (type === 'action') {
        // Enable the action button so the player can click it directly
        _updateActionButtons();
    }

    // No OK button — just a tooltip. Player clicks the actual card/pile/button.
    TutorialOverlay.showGuideOverlay(guide.text, targetEl);
}

function _updateActionButtons() {
    if (!actionControlsEl) return;
    const cardSelected = selectedCardIndex >= 0;
    const pilesSelected = selectedPileIndices.length > 0;

    const sowBtn = actionControlsEl.querySelector('[data-action="sow"]');
    const harvestBtn = actionControlsEl.querySelector('[data-action="harvest"]');
    const stockpileBtn = actionControlsEl.querySelector('[data-action="stockpile"]');
    const cancelBtn = actionControlsEl.querySelector('[data-action="cancel"]');

    if (sowBtn) sowBtn.disabled = !cardSelected;
    if (harvestBtn) harvestBtn.disabled = !(cardSelected && pilesSelected);
    if (stockpileBtn) stockpileBtn.disabled = !(cardSelected && pilesSelected);
    if (cancelBtn) cancelBtn.disabled = !(cardSelected || pilesSelected);
}

// --- Target resolution ---

function _resolveTarget(target) {
    if (!target) return null;

    const [type, param] = target.split(':');

    switch (type) {
        case 'hand-card': {
            // Find the card element in the hand matching the card string
            const card = parseCardString(param);
            if (!handContentEl) return null;
            const cardEls = handContentEl.querySelectorAll('.card');
            for (const el of cardEls) {
                if (el.dataset.suit === card.suit && el.dataset.value === card.value) {
                    return el;
                }
            }
            return null;
        }
        case 'board-pile': {
            // Find pile element(s) by data index (not DOM position — board sorts by value)
            const indices = param.split(',').map(Number);
            if (!boardContentEl) return null;
            const findPile = (idx) => boardContentEl.querySelector(`.stack-container[data-pile-index="${idx}"]`);
            if (indices.length === 1) {
                return findPile(indices[0]) || null;
            }
            const elements = indices.map(findPile).filter(Boolean);
            return elements.length > 0 ? elements : null;
        }
        case 'action': {
            if (!actionControlsEl) return null;
            return actionControlsEl.querySelector(`[data-action="${param}"]`);
        }
        default:
            return null;
    }
}

// --- Interaction handling ---

function _attachListeners() {
    handListener = (e) => _handleHandSelection(e);
    boardListener = (e) => _handleBoardSelection(e);
    actionListener = (e) => _handleActionClick(e);

    if (handContentEl) {
        handContentEl.addEventListener('krida-hand-selection-changed', handListener);
    }
    if (boardContentEl) {
        boardContentEl.addEventListener('krida-board-selection-changed', boardListener);
    }
    if (actionControlsEl) {
        actionControlsEl.addEventListener('click', actionListener);
    }
}

function _detachListeners() {
    if (handContentEl && handListener) {
        handContentEl.removeEventListener('krida-hand-selection-changed', handListener);
    }
    if (boardContentEl && boardListener) {
        boardContentEl.removeEventListener('krida-board-selection-changed', boardListener);
    }
    if (actionControlsEl && actionListener) {
        actionControlsEl.removeEventListener('click', actionListener);
    }
    handListener = null;
    boardListener = null;
    actionListener = null;
}

function _handleHandSelection(e) {
    if (!active) return;
    const step = scenarioData.steps[currentStepIndex];
    if (!step || step.type !== 'player') return;

    const detail = e.detail;
    if (!detail) return;

    selectedCardIndex = detail.selectedIndices?.length > 0 ? detail.selectedIndices[0] : -1;

    if (selectedCardIndex >= 0 && step.expectedCard) {
        const selectedCard = currentHand[selectedCardIndex];
        const expectedCard = parseCardString(step.expectedCard);
        if (selectedCard && selectedCard.suit === expectedCard.suit && selectedCard.value === expectedCard.value) {
            _advanceGuide('hand-card:' + step.expectedCard);
        } else {
            TutorialOverlay.showGuideOverlay(
                `Try selecting the ${step.expectedCard} instead`,
                _resolveTarget('hand-card:' + step.expectedCard)
            );
        }
    }
    _updateActionButtons();
}

function _handleBoardSelection(e) {
    if (!active) return;
    const step = scenarioData.steps[currentStepIndex];
    if (!step || step.type !== 'player') return;

    const detail = e.detail;
    if (!detail) return;

    const pileIndex = detail.pileIndex;
    if (pileIndex !== undefined) {
        const idx = selectedPileIndices.indexOf(pileIndex);
        if (idx >= 0) {
            selectedPileIndices.splice(idx, 1);
        } else {
            selectedPileIndices.push(pileIndex);
        }
    }

    // Re-render board with current selections
    KridaUIBoard.updateBoard(currentBoard, selectedPileIndices, true);

    // Check if the correct piles are now selected
    if (step.expectedPiles && step.expectedPiles.length > 0) {
        const expected = [...step.expectedPiles].sort();
        const selected = [...selectedPileIndices].sort();
        if (expected.length === selected.length && expected.every((v, i) => v === selected[i])) {
            _advanceGuide('board-pile:' + step.expectedPiles.join(','));
        }
    }
    _updateActionButtons();
}

function _handleActionClick(e) {
    if (!active) return;
    const btn = e.target.closest('[data-action]');
    if (!btn) return;

    const action = btn.dataset.action;
    const step = scenarioData.steps[currentStepIndex];
    if (!step || step.type !== 'player') return;

    if (action === step.expectedAction) {
        TutorialOverlay.dismiss();
        _executeAction(step);
    } else if (action === 'cancel') {
        _clearSelections();
        KridaUIBoard.updateBoard(currentBoard, [], true);
        KridaUIHand.updateHand(currentHand, [], true);
        currentGuideIndex = 0;
        _showGuideStep();
    }
}

function _advanceGuide(target) {
    const step = scenarioData.steps[currentStepIndex];
    if (!step.guideSteps) return;

    if (currentGuideIndex < step.guideSteps.length) {
        const guide = step.guideSteps[currentGuideIndex];
        if (guide.target === target) {
            currentGuideIndex++;
            TutorialOverlay.dismiss();
            if (currentGuideIndex < step.guideSteps.length) {
                setTimeout(() => _showGuideStep(), 200);
            }
        }
    }
}

function _executeAction(step) {
    TutorialOverlay.dismiss();

    const playedCard = currentHand.splice(selectedCardIndex, 1)[0];

    if (step.expectedAction === 'sow') {
        // Same as PatraGame.performSowAction
        currentBoard.push({ visible: [playedCard], hidden: [] });

    } else if (step.expectedAction === 'harvest') {
        // Same as PatraGame.performHarvestAction
        const harvestedCards = [playedCard];
        const sortedPiles = [...selectedPileIndices].sort((a, b) => b - a);
        sortedPiles.forEach(idx => {
            const pile = currentBoard[idx];
            if (pile.visible) harvestedCards.push(...pile.visible);
            if (pile.hidden) harvestedCards.push(...pile.hidden);
            currentBoard.splice(idx, 1);
        });
        let points = 0;
        harvestedCards.forEach(c => { points += getCardPointValue(c); });
        const clearedBoard = currentBoard.length === 0;
        if (clearedBoard) points += 25;
        tutorialHarvests.push({ cards: harvestedCards, points, clearedBoard });
        KridaUIHarvest.updateHarvest(tutorialHarvests);

    } else if (step.expectedAction === 'stockpile') {
        // Use real validation helper — same as PatraGame.performStockpileAction
        const fakeState = _buildFakeGameState();
        const tempHand = [...currentHand];
        tempHand.splice(selectedCardIndex, 0, playedCard);
        fakeState.player1Hand = tempHand;
        const options = findValidStockpileOptions(fakeState, selectedCardIndex, selectedPileIndices);

        if (options.length > 0) {
            const chosenOption = options[0];
            const { targetValue } = chosenOption;
            const stockpileType = chosenOption.type;
            const sumIndices = chosenOption.sumIndices || [];

            // Collect all cards from involved piles
            const allInvolvedVisible = [];
            const allInvolvedHidden = [];
            selectedPileIndices.forEach(idx => {
                const pile = currentBoard[idx];
                if (pile.visible) allInvolvedVisible.push(...pile.visible);
                if (pile.hidden) allInvolvedHidden.push(...pile.hidden);
            });

            const playedCardValue = getCardNumericValue(playedCard);
            const allCards = [playedCard, ...allInvolvedVisible, ...allInvolvedHidden];

            // Determine visible/hidden — mirrors PatraGame lines 736-758
            let newVisible = [];
            if (stockpileType === 'sum') {
                // Sum type: played card + base (sum) pile cards are visible
                newVisible = [playedCard];
                sumIndices.forEach(idx => {
                    const pile = currentBoard[idx];
                    if (pile && pile.visible) newVisible.push(...pile.visible);
                });
            } else {
                // Match type: just the played card visible
                newVisible = [playedCard];
            }

            let newHidden = allCards.filter(card =>
                !newVisible.some(vc => vc.suit === card.suit && vc.value === card.value)
            );

            // Deduplicate (mirrors PatraGame lines 762-779)
            const visibleSet = new Set();
            newVisible = newVisible.filter(card => {
                const key = `${card.value}-${card.suit}`;
                if (!visibleSet.has(key)) { visibleSet.add(key); return true; }
                return false;
            });
            const hiddenSet = new Set();
            newHidden = newHidden.filter(card => {
                const key = `${card.value}-${card.suit}`;
                if (!visibleSet.has(key) && !hiddenSet.has(key)) { hiddenSet.add(key); return true; }
                return false;
            });

            const newPile = { visible: newVisible, hidden: newHidden };

            // Remove old piles (descending order)
            const sortedPiles = [...selectedPileIndices].sort((a, b) => b - a);
            sortedPiles.forEach(idx => currentBoard.splice(idx, 1));

            currentBoard.push(newPile);
        }
    }

    // Re-render
    KridaUIBoard.updateBoard(currentBoard, [], true);
    KridaUIHand.updateHand(currentHand, [], true);
    _clearSelections();

    // Show success text, highlighting the relevant result
    if (step.successText) {
        let successTarget = null;
        if (step.expectedAction === 'sow' || step.expectedAction === 'stockpile') {
            // Highlight the newly created/modified pile (last in currentBoard array)
            const newPileIndex = currentBoard.length - 1;
            successTarget = boardContentEl.querySelector(`.stack-container[data-pile-index="${newPileIndex}"]`);
        } else if (step.expectedAction === 'harvest') {
            // Highlight the last harvested stack (most recent harvest)
            const harvestContent = document.getElementById('harvest-content');
            if (harvestContent && harvestContent.lastElementChild) {
                successTarget = harvestContent.lastElementChild;
            }
        }
        const advance = () => { currentStepIndex++; _runStep(); };
        TutorialOverlay.showGuideOverlay(step.successText, successTarget, advance, _navForOverlay(advance));
    } else {
        currentStepIndex++;
        _runStep();
    }
}

// --- Scenario completion ---

function _onScenarioComplete() {
    active = false;
    _detachListeners();
    _setTutorialMode(false);
    _clearSelections();
    _removeTempGameArea();

    // Emit completion event for krida-tutorial.js to advance the wizard
    document.dispatchEvent(new CustomEvent('krida-tutorial-scenario-complete', {
        detail: { scenarioId: scenarioData?.id }
    }));
}

// --- UI helpers ---

function _buildFakeGameState() {
    return {
        player1Hand: currentHand,
        player2Hand: null,
        board: currentBoard,
        currentPlayer: 0,
        options: { stockpileDisplayPreference: 'base', bonusClearBoard: 25 }
    };
}

function _clearSelections() {
    selectedCardIndex = -1;
    selectedPileIndices = [];
}

function _setTutorialMode(enabled) {
    // Toggle visibility of non-tutorial game elements
    const elementsToHide = [
        'game-info', 'player-scores'
    ];
    elementsToHide.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.display = enabled ? 'none' : '';
    });

    // Add/remove tutorial mode class to game container
    const gameContainer = document.getElementById('game-container');
    if (gameContainer) {
        gameContainer.classList.toggle('tutorial-mode', enabled);
    }
}

function _setActionButtons() {
    if (!actionControlsEl) return;
    const surrender = actionControlsEl.querySelector('[data-action="surrender"]');
    if (surrender) surrender.style.display = 'none';
}

// --- Temporary game area (for non-game pages like dashboard) ---

const REQUIRED_CSS = [
    '/css/game/layout.css',
    '/css/game/header.css',
    '/css/game/game-stats.css',
    '/css/game/messages.css',
    '/css/game/board.css',
    '/css/game/card-styles.css',
    '/css/game/hand-area.css',
    '/css/game/stack-container.css',
    '/css/game/actions.css',
    '/css/game/harvest.css',
    '/css/game/player-score.css',
    '/css/game/tutorial-overlay.css'
];

async function _createTempGameArea() {
    usingTempGameArea = true;

    // Load required CSS that the dashboard page doesn't have
    const cssPromises = REQUIRED_CSS.map(href => {
        // Skip if already loaded
        if (document.querySelector(`link[href="${href}"]`)) return Promise.resolve();
        return new Promise(resolve => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.onload = resolve;
            link.onerror = resolve; // Don't block on missing CSS
            document.head.appendChild(link);
            loadedStylesheets.push(link);
        });
    });
    await Promise.all(cssPromises);

    // Hide existing page content
    const existingContent = document.body.children;
    for (const child of existingContent) {
        if (!child.matches('script, link, style')) {
            child.dataset.tutorialHidden = child.style.display;
            child.style.display = 'none';
        }
    }

    // Create the game area DOM using the same structure/classes as krida.html
    tempGameArea = document.createElement('div');
    tempGameArea.id = 'tutorial-temp-game-area';
    tempGameArea.innerHTML = `
        <div id="header-container"></div>
        <div class="main-content-wrapper">
            <div class="game-stats">
                <div class="gamestats-player-score player1-score-area">
                    <span class="player-name" id="player1-name">You</span>
                    <span class="player-score" id="player1-score">0</span>
                </div>
                <div class="center-stats">
                    <div id="round-info">Round: 1</div>
                    <div id="deck-info">Deck: 0 Cards</div>
                </div>
                <div class="gamestats-player-score player2-score-area">
                    <span class="player-name" id="player2-name">Computer</span>
                    <span class="player-score" id="player2-score">0</span>
                </div>
            </div>
            <div class="message-container message rounded-margin">
                <div id="game-message-line1">Tutorial</div>
                <div id="game-message-line2">&nbsp;</div>
            </div>
            <div class="board rounded-margin" id="board">
                <div id="board-content"></div>
            </div>
            <div id="action-controls" class="actions">
                <button class="action-btn" data-action="sow" disabled>Sow</button>
                <button class="action-btn" data-action="harvest" disabled>Harvest</button>
                <button class="action-btn" data-action="stockpile" disabled>Stockpile</button>
                <button class="action-btn" data-action="cancel" disabled>Deselect All</button>
            </div>
            <div class="hand rounded-margin" id="hand-area">
                <div id="hand-content"></div>
            </div>
            <div class="harvest rounded-margin" id="harvest-area">
                <div id="harvest-content"></div>
            </div>
        </div>
    `;

    document.body.appendChild(tempGameArea);
}

function _removeTempGameArea() {
    if (!usingTempGameArea) return;

    if (tempGameArea) {
        tempGameArea.remove();
        tempGameArea = null;
    }

    // Restore hidden page content
    const hiddenElements = document.querySelectorAll('[data-tutorial-hidden]');
    for (const el of hiddenElements) {
        el.style.display = el.dataset.tutorialHidden || '';
        delete el.dataset.tutorialHidden;
    }

    // Remove dynamically loaded CSS
    loadedStylesheets.forEach(link => link.remove());
    loadedStylesheets = [];

    usingTempGameArea = false;
}
